import type { DivisionProfile, DivisionBaseline } from '@/lib/types/division-profile';
import { CategoryRow } from './CategoryRow';
import { DistributionChart } from './DistributionChart';
import { DivisionSummaryPanel } from './DivisionSummaryPanel';
import { KpiCard, KpiRow } from './KpiCard';
import { KpiWithUnits } from './KpiWithUnits';
import { KpiHeliborne } from './KpiHeliborne';
import {
  getOpticsTierLabel,
  opticsTierClassName,
} from '@/lib/utils/display-optics';
import { getPercentile } from '@/lib/utils/percentile';
import {
  Truck,
  Shield,
  Crosshair,
  Target,
  ShieldAlert,
  Flame,
  Plane,
  Users,
} from 'lucide-react';

interface DivisionBriefingDisplayProps {
  profile: DivisionProfile;
  baseline: DivisionBaseline;
}

export function DivisionBriefingDisplay({
  profile,
  baseline,
}: DivisionBriefingDisplayProps) {
  const thirtyPenAtCards = profile.overview.thirtyPenATUnits.reduce(
    (sum, u) => sum + u.cards,
    0
  );

  const infFieldablePct =
    baseline.sortedValues['infFieldableModels']?.length
      ? Math.round(
          getPercentile(
            baseline.sortedValues['infFieldableModels'],
            profile.infantry.fieldableInfModels
          )
        )
      : undefined;
  const tankFieldablePct =
    baseline.sortedValues['tankFieldableModels']?.length
      ? Math.round(
          getPercentile(
            baseline.sortedValues['tankFieldableModels'],
            profile.tanks.fieldableModels
          )
        )
      : undefined;

  const maxAsfAamRange = profile.planes.maxAsfMissileRange;
  const aamBench = baseline.aamPlaneRangeBenchmark;
  let maxAsfAamVsCatalogSub: string | undefined;
  if (aamBench && aamBench.length > 0 && maxAsfAamRange > 0) {
    const pct = Math.round(getPercentile(aamBench, maxAsfAamRange));
    const longest = aamBench[aamBench.length - 1]!;
    maxAsfAamVsCatalogSub = `Higher than ${pct}% of AA missile ranges (max ${longest.toLocaleString()}m)`;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-1.5">
      <div className="lg:row-span-2">
        <DivisionSummaryPanel profile={profile} baseline={baseline} />
      </div>

      <CategoryRow label="Logistics" icon={Truck} profile={profile.logistics}>
        <KpiRow>
          <KpiCard label="Supply Units (pack types)" value={profile.logistics.supplyUnitCount} />
          <KpiCard label="Total Capacity" value={profile.logistics.totalSupplyCapacity.toLocaleString()} />
          <KpiCard label="FOBs (pack types)" value={profile.logistics.fobCount} />
          <KpiWithUnits
            label="CV (cards)"
            value={profile.logistics.cvCount}
            units={profile.logistics.cvUnits}
          />
          <KpiWithUnits
            label="FWD CV (cards)"
            value={profile.logistics.fwdCvCount}
            units={profile.logistics.fwdCvUnits}
          />
        </KpiRow>
      </CategoryRow>

      <CategoryRow label="Infantry" icon={Users} profile={profile.infantry}>
        <KpiRow>
          <KpiCard
            label="Fieldable Models"
            value={profile.infantry.fieldableInfModels}
            percentile={infFieldablePct}
          />
          <KpiWithUnits label="FWD Infantry (cards)" value={profile.infantry.fwdInfCount} units={profile.infantry.fwdInfUnits} />
          <KpiWithUnits label="CV (cards)" value={profile.infantry.cvCount} units={profile.infantry.cvUnits} />
          <KpiWithUnits label="Elite / SF (cards)" value={profile.infantry.eliteSfCount} units={profile.infantry.eliteSfUnits} />
          <KpiWithUnits label="IFV Types" value={profile.infantry.ifvCount} units={profile.infantry.ifvUnits} />
          <KpiHeliborne
            label="Heliborne (cards)"
            value={profile.infantry.heliborneCardCount}
            entries={profile.infantry.heliborneEntries}
          />
        </KpiRow>
        <KpiRow>
          <KpiWithUnits label="ATGM Squads (cards)" value={profile.infantry.atmgSquadCount} units={profile.infantry.atmgSquadUnits} />
          <KpiWithUnits label="ATGM Infantry (cards)" value={profile.infantry.atmgInfCount} units={profile.infantry.atmgInfUnits} />
          <KpiWithUnits label="FWD AT INF (cards)" value={profile.infantry.fwdATInfCount} units={profile.infantry.fwdATInfUnits} />
          <KpiCard
            label="Best AT Pen"
            value={profile.infantry.highestInfATPen > 0
              ? `${profile.infantry.highestInfATPen} AP`
              : '—'}
          />
        </KpiRow>
      </CategoryRow>

      <CategoryRow label="Tanks" icon={Shield} profile={profile.tanks}>
        <KpiRow>
          <KpiCard
            label="Heaviest Armor"
            value={profile.tanks.heaviestTank
              ? `${profile.tanks.heaviestTank.value} FA`
              : '—'}
          />
          <KpiCard
            label="Fieldable Models"
            value={profile.tanks.fieldableModels}
            percentile={tankFieldablePct}
          />
          <KpiWithUnits label="ATGM Vehicles (cards)" value={profile.tanks.atmgVehicleCount} units={profile.tanks.atmgVehicleUnits} />
          <KpiWithUnits
            label="Active protection (cards)"
            value={profile.tanks.activeProtectionCardCount}
            units={profile.tanks.activeProtectionUnits}
          />
          <KpiWithUnits label="CV (cards)" value={profile.tanks.cvCount} units={profile.tanks.cvUnits} />
        </KpiRow>
        <KpiRow>
          <KpiWithUnits label="30+ Pen AT (cards)" value={thirtyPenAtCards} units={profile.overview.thirtyPenATUnits} />
        </KpiRow>
        <DistributionChart
          data={profile.tanks.armorHistogram}
          label="Front armor (fieldable models)"
          height={110}
          sortBy="asGiven"
          yAxisWidth={72}
          valueFormatter={(v) => `${Math.round(v)} models`}
        />
      </CategoryRow>

      <CategoryRow label="Recon" icon={Crosshair} profile={profile.recon}>
        <KpiRow>
          <KpiWithUnits label="FWD Recon INF (cards)" value={profile.recon.fwdReconInfCount} units={profile.recon.fwdReconInfUnits} />
          <KpiCard
            label="Best Optics"
            value={
              profile.recon.bestOpticsValue > 0
                ? getOpticsTierLabel(profile.recon.bestOpticsValue)
                : '—'
            }
            valueClassName={
              profile.recon.bestOpticsValue > 0
                ? opticsTierClassName(profile.recon.bestOpticsValue)
                : undefined
            }
            subValue={
              profile.recon.bestOpticsValue > 0
                ? `${profile.recon.bestOpticsValue}`
                : undefined
            }
            subValueClassName="text-muted-foreground"
          />
          <KpiWithUnits label="Exceptional Optics (cards)" value={profile.recon.exceptionalOpticsCount} units={profile.recon.exceptionalOpticsUnits} />
          <KpiWithUnits label="Recon tanks (cards)" value={profile.recon.reconTankCount} units={profile.recon.reconTankUnits} />
          <KpiWithUnits label="REC AFVs (cards)" value={profile.recon.reconAfvCount} units={profile.recon.reconAfvUnits} />
        </KpiRow>
      </CategoryRow>

      <CategoryRow label="Anti-Air" icon={ShieldAlert} profile={profile.antiAir}>
        <KpiRow>
          <KpiWithUnits label="Radar AA (cards)" value={profile.antiAir.radarAACount} units={profile.antiAir.radarAAUnits} />
          <KpiWithUnits label="FWD AA (cards)" value={profile.antiAir.fwdAACount} units={profile.antiAir.fwdAAUnits} />
          <KpiWithUnits label="AA Heli (cards)" value={profile.antiAir.aaHeliCount} units={profile.antiAir.aaHeliUnits} />
          <KpiCard
            label="Max Heli Range"
            value={profile.antiAir.maxHeliRange > 0 ? `${profile.antiAir.maxHeliRange}m` : '—'}
          />
          <KpiCard
            label="Max Plane Range"
            value={profile.antiAir.maxPlaneRange > 0 ? `${profile.antiAir.maxPlaneRange}m` : '—'}
          />
        </KpiRow>
      </CategoryRow>

      <CategoryRow label="Artillery" icon={Target} profile={profile.artillery}>
        <KpiRow>
          <KpiCard label="Mortars (pack types)" value={profile.artillery.mortarCount} />
          <KpiCard
            label="Howitzers (pack types)"
            value={profile.artillery.howitzerCount}
            subValue={
              profile.artillery.howitzerCount > 0
                ? [
                    profile.artillery.heaviestHowitzerMm > 0
                      ? `heaviest — ${profile.artillery.heaviestHowitzerMm}mm`
                      : null,
                    profile.artillery.maxHeHowitzer > 0
                      ? `max HE ${profile.artillery.maxHeHowitzer.toFixed(2)}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || undefined
                : undefined
            }
          />
          <KpiCard label="MLRS (pack types)" value={profile.artillery.rocketCount} />
        </KpiRow>
        <KpiRow>
          <KpiCard label="Cluster (pack types)" value={profile.artillery.clusterMLRSCount} />
          <KpiCard label="Napalm (pack types)" value={profile.artillery.napalmMLRSCount} />
          <KpiCard label="Thermobaric (pack types)" value={profile.artillery.thermobaricCount} />
        </KpiRow>
      </CategoryRow>

      <CategoryRow label="Helicopters" icon={Flame} profile={profile.helicopters}>
        <KpiRow>
          <KpiWithUnits
            label="Light attack (cards)"
            value={profile.helicopters.lightAttackHeliCount}
            units={profile.helicopters.lightAttackHeliUnits}
          />
          <KpiWithUnits label="Gunships (cards)" value={profile.helicopters.gunshipCount} units={profile.helicopters.gunshipUnits} />
          <KpiWithUnits
            label="AA Heli in AA tab (cards)"
            value={profile.antiAir.aaHeliCount}
            units={profile.antiAir.aaHeliUnits}
          />
          <KpiCard
            label="Best AT Pen"
            value={profile.helicopters.highestHeliATPen > 0
              ? `${profile.helicopters.highestHeliATPen} AP`
              : '—'}
          />
        </KpiRow>
      </CategoryRow>

      <CategoryRow label="Planes" icon={Plane} profile={profile.planes}>
        <KpiRow>
          <KpiWithUnits label="ASF (pack types)" value={profile.planes.asfCount} units={profile.planes.asfUnits} />
          <KpiCard
            label="Max ASF AAM"
            value={profile.planes.maxAsfMissileRange > 0
              ? `${profile.planes.maxAsfMissileRange}m`
              : '—'}
            subValue={maxAsfAamVsCatalogSub}
          />
          <KpiWithUnits label="SEAD (pack types)" value={profile.planes.seadCount} units={profile.planes.seadUnits} />
          <KpiWithUnits label="LGB (pack types)" value={profile.planes.lgbCount} units={profile.planes.lgbUnits} />
          <KpiWithUnits label="EW (cards)" value={profile.planes.ewCount} units={profile.planes.ewUnits} />
          <KpiWithUnits label="Bomber / Multi (pack types)" value={profile.planes.bomberMultiroleCount} units={profile.planes.bomberMultiroleUnits} />
        </KpiRow>
      </CategoryRow>
    </div>
  );
}
